/* LumaSpec shell behaviour (SHELL lane): header heights + sticky state, mega menu, phone drawer, inline predictive
   search (CATALOG's ls-search-predictive section; model numbers via variants.sku + exact handle) and the quote list API. Loaded once as a module from layout/theme.liquid.

   Quote list contract (for PRODUCT / CATALOG lanes):
     window.LSQuote.add({ v, sku, t, h, i, q, o })  // variant id, SKU, title, handle, image url, qty, {option: value}
     window.LSQuote.list() / .save(list) / .count()
     document event 'ls:quote-change' (detail = list); any [data-quote-count] element shows the count. */

const root = document.documentElement;
root.classList.add('ls-js');
/** UI strings from locales/en.default.json (ls.shell.*), printed by layout/theme.liquid */
const T = { noResults: 'No products match {q}', allResults: 'See all results for “{q}”', ...window.LSStrings };

/* the t filter HTML-escapes plain (non-_html) keys: back to plain text once (shown via textContent / esc()) */
for (const k of Object.keys(T)) {
  if (typeof T[k] === 'string' && T[k].includes('&')) {
    const d = document.createElement('textarea');
    d.innerHTML = T[k];
    T[k] = d.value;
  }
}

/* ---------------- quote list (same storage key + item shape as the Pages site) ---------------- */
const QKEY = 'lumaspec-quote-v1';
let qmem = [];
const LSQuote = {
  KEY: QKEY,
  list() {
    try {
      return JSON.parse(localStorage.getItem(QKEY) || '[]') || [];
    } catch {
      return qmem;
    }
  },
  save(list) {
    qmem = list;
    try {
      localStorage.setItem(QKEY, JSON.stringify(list));
    } catch {
      /* storage blocked: the list lasts for this page */
    }
    paintQuote();
    document.dispatchEvent(new CustomEvent('ls:quote-change', { detail: list }));
  },
  add(item) {
    const key = (x) => `${x.h}|${x.sku}|${JSON.stringify(x.o || null)}`;
    const list = LSQuote.list();
    const hit = list.find((x) => key(x) === key(item));
    if (hit) hit.q += item.q || 1;
    else list.push({ ...item, q: item.q || 1 });
    LSQuote.save(list);
    return list;
  },
  count() {
    return LSQuote.list().length;
  },
};
window.LSQuote = LSQuote;
function paintQuote() {
  const n = LSQuote.count();
  for (const el of document.querySelectorAll('[data-quote-count]')) el.textContent = n ? String(n) : '';
}
window.addEventListener('storage', (e) => e.key === QKEY && paintQuote());

/* ---------------- header: heights for Horizon's sticky consumers + stuck shadow ---------------- */
function initHeader() {
  const header = document.querySelector('[data-ls-header]');
  if (!header) return;
  const group = document.getElementById('header-group');
  const setHeights = () => {
    // Read the complete geometry before writing inherited styles: a write between offsetHeight reads
    // forces another full layout. ResizeObserver also delivers once when nothing has changed.
    const headerHeight = header.offsetHeight;
    let g = 0;
    if (group) for (const el of group.children) if (el instanceof HTMLElement) g += el.offsetHeight;
    const values = [['--header-height', `${headerHeight}px`], ['--header-group-height', `${g || headerHeight}px`]];
    for (const [name, value] of values) {
      if (document.body.style.getPropertyValue(name) !== value) document.body.style.setProperty(name, value);
    }
  };
  setHeights();
  new ResizeObserver(setHeights).observe(header);
  const wrapper = document.querySelector('.page-wrapper');
  const desktop = window.matchMedia('(min-width: 990px)'); // base.css uses this breakpoint for the wrapper scroller
  let stuck = false;
  const onScroll = () => {
    const y = desktop.matches && wrapper ? wrapper.scrollTop : window.scrollY;
    const s = y > 8;
    if (s !== stuck) {
      stuck = s;
      header.classList.toggle('is-stuck', s);
      header.dataset.stickyState = s ? 'active' : 'inactive';
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  wrapper?.addEventListener('scroll', onScroll, { passive: true });
  desktop.addEventListener('change', onScroll);
  onScroll();
}

/* ---------------- mega menu: hover intent on mouse, click/keyboard everywhere ---------------- */
function initMega() {
  const items = [...document.querySelectorAll('[data-ls-mega-item]')];
  if (!items.length) return;
  let openTimer = 0;
  let closeTimer = 0;
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const setOpen = (item, open) => {
    item.classList.toggle('is-open', open);
    item.querySelector('[aria-expanded]')?.setAttribute('aria-expanded', String(open));
  };
  const closeAll = (except) => items.forEach((i) => i !== except && setOpen(i, false));
  for (const item of items) {
    const btn = item.querySelector('.ls-nav__btn');
    btn?.addEventListener('click', () => {
      const open = !item.classList.contains('is-open');
      closeAll(item);
      setOpen(item, open);
    });
    item.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'mouse' || !fine.matches) return;
      clearTimeout(closeTimer);
      clearTimeout(openTimer);
      const anyOpen = items.some((i) => i.classList.contains('is-open'));
      openTimer = window.setTimeout(() => {
        closeAll(item);
        setOpen(item, true);
      }, anyOpen ? 40 : 140);
    });
    item.addEventListener('pointerleave', (e) => {
      if (e.pointerType !== 'mouse' || !fine.matches) return;
      clearTimeout(openTimer);
      closeTimer = window.setTimeout(() => setOpen(item, false), 220);
    });
    item.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && item.classList.contains('is-open')) {
        setOpen(item, false);
        btn?.focus();
      }
    });
    item.addEventListener('focusout', (e) => {
      if (!item.contains(/** @type {Node} */ (e.relatedTarget))) setOpen(item, false);
    });
  }
  document.addEventListener('click', (e) => {
    if (!(e.target instanceof Element) || !e.target.closest('[data-ls-mega-item]')) closeAll();
  });
}

/* ---------------- phone drawer (native dialog) ---------------- */
function initDrawer() {
  const dlg = /** @type {HTMLDialogElement|null} */ (document.getElementById('ls-drawer'));
  if (!dlg) return;
  const openers = document.querySelectorAll('[data-ls-drawer-open]');
  const set = (open) => openers.forEach((b) => b.setAttribute('aria-expanded', String(open)));
  /* POLISH lane: the drawer's category tiles are copies of the mega menu's (li.ls-mega__cat in #ls-mega-<part>),
     made once on first open instead of printing the same ~45 tiles twice in every page (~4 KB gzip). */
  let filled = false;
  const fill = () => {
    if (filled) return;
    filled = true;
    for (const acc of dlg.querySelectorAll('[data-ls-acc]')) {
      const grid = acc.querySelector('[data-ls-acc-grid]');
      const src = document.getElementById(`ls-mega-${/** @type {HTMLElement} */ (acc).dataset.lsAcc}`);
      if (!grid || !src) continue;
      const frag = document.createDocumentFragment();
      for (const li of src.querySelectorAll('li.ls-mega__cat')) {
        const copy = /** @type {HTMLElement} */ (li.cloneNode(true));
        for (const img of copy.querySelectorAll('img[sizes]')) /** @type {HTMLImageElement} */ (img).sizes = '45vw';
        frag.append(copy);
      }
      grid.append(frag);
    }
  };
  openers.forEach((b) =>
    b.addEventListener('click', () => {
      if (dlg.open) return dlg.close();
      fill();
      dlg.showModal();
      set(true);
    })
  );
  dlg.querySelector('[data-ls-drawer-close]')?.addEventListener('click', () => dlg.close());
  dlg.addEventListener('close', () => set(false));
  dlg.addEventListener('click', (e) => e.target === dlg && dlg.close());
}

/* ---------------- inline predictive search ---------------- */
function initSearch(form, n) {
  const input = /** @type {HTMLInputElement} */ (form.querySelector('input[name="q"]'));
  const panel = /** @type {HTMLElement} */ (form.querySelector('[data-ls-search-panel]'));
  if (!input || !panel) return;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  let ctrl = null;
  let timer = 0;
  let active = -1;

  const close = () => {
    panel.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    active = -1;
  };
  const options = () => [...panel.querySelectorAll('[role="option"]')];
  const highlight = (i) => {
    const opts = options();
    if (!opts.length) return;
    active = (i + opts.length) % opts.length;
    opts.forEach((o, j) => o.setAttribute('aria-selected', String(j === active)));
    input.setAttribute('aria-activedescendant', opts[active].id);
    opts[active].scrollIntoView({ block: 'nearest' });
  };

  const run = async () => {
    const q = input.value.trim();
    if (q.length < 2) return close();
    ctrl?.abort();
    const request = new AbortController();
    ctrl = request;
    // Results markup = CATALOG lane's sections/ls-search-predictive.liquid (snippets/ls-predictive-results.liquid)
    const url = new URL(`${root}search/suggest`, location.origin);
    url.searchParams.set('q', q);
    url.searchParams.set('section_id', 'ls-search-predictive');
    url.searchParams.set('resources[type]', 'product,collection,query');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.sku,vendor');
    try {
      const res = await fetch(url, { signal: request.signal });
      if (!res.ok) throw new Error(String(res.status));
      const responseText = await res.text();
      if (request !== ctrl || q !== input.value.trim() || !form.contains(document.activeElement)) return;
      const doc = new DOMParser().parseFromString(responseText, 'text/html');
      const box = doc.querySelector('.shopify-section') || doc.body;
      let html = box.innerHTML.trim();
      if (!html) {
        const all = `${root}search?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last`;
        html = `<p class="ls-search__empty">${esc(T.noResults).replace('{q}', () => `<b>${esc(q)}</b>`)}</p><div class="ls-search__all"><a class="ls-link" href="${esc(all)}">${esc(T.allResults).replace('{q}', () => esc(q))}</a></div>`;
      }
      panel.innerHTML = html;
      // ids stay unique when two search forms share a page
      panel.querySelectorAll('[role="option"]').forEach((o, i) => (o.id = `ls-sr${n}-${i}`));
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      active = -1;
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      close();
    }
  };

  input.addEventListener('input', () => {
    clearTimeout(timer);
    ctrl?.abort();
    if (input.value.trim().length < 2) return close();
    timer = window.setTimeout(run, 160);
  });
  input.addEventListener('focus', () => input.value.trim().length >= 2 && panel.innerHTML && (panel.hidden = false));
  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (panel.hidden) run();
      else highlight(active + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      highlight(active - 1);
    } else if (e.key === 'Enter' && active >= 0) {
      const opt = options()[active];
      const link = opt instanceof HTMLAnchorElement ? opt : opt?.querySelector('a');
      if (link) {
        e.preventDefault();
        location.href = link.href;
      }
    } else if (e.key === 'Escape') {
      close();
    }
  });
  document.addEventListener('click', (e) => {
    if (!(e.target instanceof Node) || !form.contains(e.target)) close();
  });
  form.addEventListener('focusout', (e) => {
    if (!form.contains(/** @type {Node} */ (e.relatedTarget))) window.setTimeout(close, 120);
  });

}

function initSearchFocus() {
  const input = /** @type {HTMLInputElement|null} */ (document.getElementById('ls-q'));
  if (!input) return;
  for (const b of document.querySelectorAll('[data-ls-search-focus]')) {
    b.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      input.focus({ preventScroll: true });
    });
  }
}

function init() {
  paintQuote();
  initHeader();
  initMega();
  initDrawer();
  document.querySelectorAll('[data-ls-search]').forEach((f, n) => initSearch(/** @type {HTMLFormElement} */ (f), n));
  initSearchFocus();
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();
